/* eslint-disable max-len */
const {isFinanceUser} = require('../utils');

/**
 * Timezone that is used to define Duet session times
 * @type {string}
 */
const TIMEZONE = 'America/New_York';

/**
 * Duration of time that a user has to rejoin a P2P Duet session
 * @type {number} minutes
 */
const MAX_REJOIN_TIME = 60;

/**
 * Duration of time in which we bother users for unsubmitted feedback
 * @type {number} minutes
 */
const MAX_FEEDBACK_TIME = 4320; // 3 days

/**
 * Duration of time leading up the session during which users can
 * connect to be matched. 10 minutes.
 * @type {number} seconds
 */
const LEADING_JOIN_TIME = 600;

/**
 * Array of additonal times at which users will be matched, following the
 * scheduled P2P session, to allow for latecomers. Currently, users can be
 * matched 2 minutes and 4 minutes after the scheduled session.
 * @type {array} array of seconds
 */
const TRAILING_MATCH_TIMES = [120, 240];

/**
 * Duration of time in which we display a countdown for an upcoming P2P session
 * @type {number} seconds
 */
const WINDOW_START_TIME = 3600; // 60 minutes

/**
 * Rolling window of days over which Duet credits are counted and after
 * which they expire: free users can join max DUET_SESSIONS_PER_PERIOD
 * P2P sessions, all users can join MAX_MONTHLY_AI_SESSIONS AI sessions
 * @type {number} days
 */
const DUET_CREDIT_TTL = 30;

/**
 * Number of P2P Duet sessions a free user can join in DUET_CREDIT_TTL
 * @type {number} sessions
 */
const DUET_SESSIONS_PER_PERIOD = 5;

/**
 * Number of times a user should receive AI transcription and feedback on
 * P2P sessions in a month. This can be exceeded if user's partner is
 * eligible for AI transcription.
 * @type {number} sessions
 */
const MAX_MONTHLY_AI_TRANSCRIPTIONS = 20;

/**
 * Number of times a user can do a single player AI session in a month. This
 * applies to all users, including paid, and is primarily used to prevent abuse.
 * TODO: Consider removing
 * @type {number}
 */
const MAX_MONTHLY_AI_SESSIONS = 50;

/**
 * Used to determine why duet_request.credit_refunded is true
 * @type {object}
 */
const CreditRefundReasons = {
  ADMIN_REFUNDED: 'admin_refunded',
  POSITIVE_PARTNER_FEEDBACK: 'positive_partner_feedback',
  USER_REPORTED_DIDNT_HAPPEN: 'user_reported_didnt_happen',
};

/**
 * For identifying Twilio screenshare video track
 */
const SCREENSHARE_TRACK = 'SCREENSHARE_TRACK';

/**
 * Array of types of mock interview a user can join
 *
 * Flags:
 *
 * hasCoding - Whether the interview type utilizes the code editor
 * hasPreassignedQuestions - Whether the interview type has preassigned questions in InterviewModes.Scheduled mode
 * hasAIPractice - Whether the interview type supports the InterviewModes.AI mode
 * hasAIFeedback - Whether the interview type supports AI feedback following InterviewModes.Scheduled sessions
 * aiSessionTTL - Time limit for AI practice sessions in seconds
 *
 * @type {array}
 */
const InterviewTypesArray = [
  {
    name: 'Product Management',
    key: 'PM',
    description: 'Practice product sense, estimation, and more.',
    hasAIPractice: true,
    hasAIFeedback: true,
    aiSessionTTL: 1200, // 20 minutes
    tags: {
      role: 'pm',
    },
  },
  {
    name: 'Data Structures & Algorithms',
    key: 'Algorithms',
    description: 'Practice coding questions.',
    hasCoding: true,
    hasPreassignedQuestions: true,
    hasAIPractice: true,
    hasAIFeedback: true,
    aiSessionTTL: 1200, // 20 minutes
    tags: {
      role: 'swe',
      type: 'algorithms',
    },
    coding: {
      defaultLanguage: 'python',
    },
  },
  {
    name: 'System Design',
    key: 'System-Design',
    description: 'Practice designing technical architectures.',
    hasAIPractice: true,
    hasAIFeedback: true,
    aiSessionTTL: 1200, // 20 minutes
    tags: {
      type: 'system-design',
    },
  },
  {
    name: 'Behavioral',
    key: 'Behavioral',
    description: 'Practice questions about your work experiences.',
    hasAIPractice: true,
    hasAIFeedback: true,
    aiSessionTTL: 600, // 10 minutes
    tags: {
      type: 'behavioral',
    },
  },
  {
    name: 'Behavioral',
    key: 'Behavioral-IB',
    description: 'Practice fit questions asked in banking interviews.',
    hasAIPractice: true,
    hasAIFeedback: true,
    aiSessionTTL: 600, // 10 minutes
    tags: {
      role: 'investment-banking',
      type: 'behavioral',
    },
  },
  {
    name: 'SQL',
    key: 'SQL',
    description: 'Practice writing and optimizing SQL queries.',
    hasCoding: true,
    hasPreassignedQuestions: true,
    hasAIPractice: true,
    hasAIFeedback: true,
    aiSessionTTL: 1200, // 20 minutes
    tags: {
      type: 'sql',
    },
    coding: {
      defaultLanguage: 'postgres',
    },
  },
  {
    name: 'Data Science & ML',
    key: 'Data-Science-ML',
    description: 'Practice data questions and designing systems.',
    hasAIFeedback: true,
    tags: {
      role: 'ml-engineer',
      type: 'system-design',
    },
  },
  {
    name: 'Frontend',
    key: 'Frontend',
    description: 'Practice JavaScript with foundational exercises.',
    hasCoding: true,
    hasPreassignedQuestions: true,
    hasAIFeedback: true,
    tags: {
      role: 'swe',
    },
    coding: {
      defaultLanguage: 'javascript',
      isRestrictedToDefault: true,
    },
  },
  {
    name: 'Investment Banking',
    key: 'Investment-Banking',
    description: 'Practice technical finance and IB interview questions.',
    badge: 'New',
    hasAIPractice: true,
    hasAIFeedback: true,
    aiSessionTTL: 600, // 10 minutes
    tags: {
      role: 'investment-banking',
      // IB mocks open on behavioral questions before moving into technicals
      type: 'behavioral',
    },
  },
];

/**
 * The keys of InterviewTypesArray's elements mapped to their associated object
 * @type {object}
 */
const InterviewTypes = InterviewTypesArray.reduce((result, type) => ({
  ...result,
  [type.key]: type,
}), {});

/**
 * The types a finance candidate leads with, in the order they should appear.
 * @type {array}
 */
const FinancePriorityTypes = ['Investment-Banking', 'Behavioral-IB'];

/**
 * The interview types a given user can practice, in the order they should be
 * offered
 * @param {object} profile a user_profile row
 * @returns {array}
 */
const getInterviewTypesForUser = (profile) => {
  if (!isFinanceUser(profile)) {
    return InterviewTypesArray.filter((t) => t.key !== 'Behavioral-IB');
  }

  const types = InterviewTypesArray.filter((t) => t.key !== 'Behavioral');
  const rank = ({key}) => {
    const i = FinancePriorityTypes.indexOf(key);
    return i === -1 ? FinancePriorityTypes.length : i;
  };

  return [...types].sort((a, b) => rank(a) - rank(b));
};

/**
 * Whether a user is allowed to practice a given interview type. Guards the
 * server against a client asking for a type it shouldn't be able to see.
 * @param {string} interviewType
 * @param {object} profile a user_profile row
 * @returns {boolean}
 */
const isInterviewTypeAvailableToUser = (interviewType, profile) => (
  getInterviewTypesForUser(profile).some((t) => t.key === interviewType)
);

/**
 * Interview types that are two flavors of the same interview for different
 * audiences, mapped to each other.
 * @type {object}
 */
const InterviewTypeCounterparts = {
  Behavioral: 'Behavioral-IB',
  'Behavioral-IB': 'Behavioral',
};

/**
 * Maps an interview type to the flavor a given user actually practices, so a
 * link or default can work for both audiences.
 * @param {string} interviewType
 * @param {object} profile a user_profile row
 * @returns {string}
 */
const resolveInterviewTypeForUser = (interviewType, profile) => {
  if (isInterviewTypeAvailableToUser(interviewType, profile)) return interviewType;

  const counterpart = InterviewTypeCounterparts[interviewType];

  return counterpart && isInterviewTypeAvailableToUser(counterpart, profile)
    ? counterpart
    : interviewType;
};

/**
 * 1P AI interview types that source questions from Duet, as opposed to Socrates
 */
const InterviewTypesWith1PAIQuestionsFromCMS = {
  [InterviewTypes.Algorithms.key]: true,
  [InterviewTypes.SQL.key]: true,
};

/**
 * Object of the different modes Duet can be used in
 * @type {object}
 */
const InterviewModes = {
  Scheduled: 'scheduled',
  OnDemand: 'on_demand',
  AI: 'ai',
  Pramp: 'pramp',
};

/**
 * The possible values for duet_request.status
 *
 * Pre-match statuses
 *
 * pending  - The request has been created, but the user has yet to
 *            connect via websocket.
 * canceled - The pending request was canceled by the user prior to
 *            matching.
 * active   - The user has connected via websocket and is waiting to be
 *            matched. Only status eligible for matching.
 * inactive - The user was previously connected via websocket, but has
 *            disconnected.
 *
 * Post-match statuses
 *
 * match    - The user was matched with another user. A duet_match for
 *            each user's request and a video room was created.
 * no_match - The user was not matched with another user.
 * no_join  - The user was matched with another user, but the user never
 *            ended up joining the session.
 *
 * @type {object}
 */
const RequestStatus = {
  PENDING: 'pending',
  CANCELED: 'canceled',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  MATCH: 'match',
  NO_MATCH: 'no_match',
  NO_JOIN: 'no_join',
};

/**
 * Following a Duet session, a user and their partner can opt in to
 * being introduced over email. These are the possible states of the
 * introduction request.
 *
 * none      - Neither user nor partner has sent intro request
 * pending   - Parner sent intro request to user
 * requested - User sent intro request to partner
 * accepted  - Both user and partner have sent intro requests
 *
 * @type {object}
 */
const PartnerIntroStatus = {
  NONE: 'none',
  PENDING: 'pending',
  REQUESTED: 'requested',
  ACCEPTED: 'accepted',
};

/**
 * Following a Duet session, AI feedback may be generated for the user
 * @type {object}
 */
const AIFeedbackStatus = {
  PENDING: 'pending',
  COMPLETE: 'complete',
  UNAVAILABLE: 'unavailable',
};

/**
 * Possible statuses for a single player AI session
 *
 * pending     - The session has been created but not started
 * started     - The session has been started, user is submitting response or feedback
 *               is being generated
 * complete    - The session has been completed, AI feedback was successfully generated
 * failed      - The session has been completed, but AI feedback was not successfully
 *               generated
 *
 * @type {object}
 */
const AISessionStatus = {
  PENDING: 'pending',
  STARTED: 'started',
  COMPLETE: 'complete',
  FAILED: 'failed',
};

/**
 * Possible reasons a 1P AI session can fail to be created
 *
 * @type {object}
 */
const AISessionCreationErrors = {
  NO_FREE_SESSIONS: 'NO_FREE_SESSIONS',
  RATE_LIMIT: 'RATE_LIMIT',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  MISSING_VERSION: 'MISSING_VERSION',
};

/**
 * The different versions of Duet 1P AI
 *
 * @type {object}
 */
const AISessionVersions = {
  realtime: 'realtime',
  transcription: 'transcription',
};

/**
 * Questions that can be assigned for single player AI Duet sessions
 * by interview type. Sourced from Socrates.
 * @type {object}
 */
const AISessionQuestionAllowlist = {
  [InterviewTypes.PM.key]: [
    'bikes-needed-launch-bike-sharing-nyc',
    'company-pain-points-favorite-product',
    'defining-success-metrics-google-flight-search',
    'design-netflix-for-kids',
    'favorite-product',
    'gmail-messages-per-second',
    'impact-faster-internet-speed',
    'improve-instagram-stories',
    'linkedin-student-product-design',
    'measure-success-chatgpt-search',
    'slack-entering-education-market',
    'time-handled-difficult-stakeholder',
    'estimate-restaurants-sf',
    'estimate-storage-google-photos',
    'calculating-tennis-balls-in-airplane',
    'empire-state',
    'design-parking-features-google-maps',
    'design-fire-alarm-for-deaf',
    'improve-spotify',
    'android-strategic-google',
    'should-google-enter-online-furniture-market',
    'pm-gmail-react-competing-product',
    'triple-revenue-of-craigslist-in-5-years',
    'meta-reels-invest-strategy-metrics',
    'convince-leadership-elderly-wristwatch',
    'monthly-active-users-decline-analysis',
    'netflix-click-through-rate-analysis',
    'determine-success-instagram-reels',
    'facebook-events-success-metrics',
    'youtube-comments-up-watch-time-down-actions',
    'product-design-rush-hour-drivers',
    'increase-users-on-youtube',
    'youtube-metrics',
    'play-store-annual-revenue',
    'should-google-enter-ridesharing-market',
    'improve-youtube-recommendation-algorithm',
    'facebook-jobs-post-launch-metrics-evaluation',
    'design-product-help-start-exercising',
    'increase-revenue-fivefold-spotify-pm',
    'design-smart-shoe',
    'lyft-pm-dashboard-app-health',
    'design-linkedin-blue-collar',
    'pm-goals-facebook-marketplaces',
    'estimate-youtube-videos',
    'samsung-enter-gaming-console-market',
    'evaluate-youtube-content-creator-tool',
    'facebook-reactions-up-comments-down',
    'improve-google-docs',
  ],
  [InterviewTypes.Behavioral.key]: [
    'conflict-resolution-learning',
    'example-managing-conflict',
    'experience-cross-functional-team',
    'how-prioritize-features',
    'mistake',
    'past-projects',
    'personal-growth-approach',
    'project-with-tight-deadline',
    'project-you-are-most-proud-of',
    'time-manage-difficult-customer',
    'time-short-term-sacrifices-long-term-gains',
    'time-when-project-failed',
    'time-disagreed-someone-resolved',
    'time-skill-recently-learned',
    'why-no-hire',
    'respond-team-disagrees',
    'solving-customer-pain-points-example',
    'prioritize-five-different-projects',
    'managing-poor-performers-team',
    'impact-of-work-on-world',
    'managing-risk-in-project',
    'tell-me-about-time-solved-complex-problem',
    'reason-switch-jobs',
    'time-proposed-idea-not-agreed',
    'time-dissatisfied-with-status-quo',
    'time-improved-complex-process',
    'biggest-failure-as-product-manager',
    'disagree-with-manager',
    'coaching-engineering-team',
    'calculated-risk-taken-speed-critical',
    'building-credibility-with-new-reports',
  ],
  [InterviewTypes['Behavioral-IB'].key]: [
    'describe-yourself-introduction-summary',
    'describe-career-journey-resume',
    'describe-nonresume-qualities-success-role',
    'explain-investment-banking-reasons',
    'describe-strengths-and-weaknesses',
    'describe-future-career-goals-five-years',
    'describe-holding-teammate-accountable-project',
    'describe-teamwork-major-goal-outcome',
    'describe-teamwork-solving-problem',
    'explain-important-factor-teamwork',
    'describe-right-decision-conflict-situation',
    'describe-going-above-beyond-work',
    'describe-extra-initiatives-results',
    'describe-handling-mistake-at-work',
    'describe-high-quality-work-tight-deadline',
    'describe-tight-deadline-limited-information',
    'describe-feedback-applied-career',
    'explain-prioritize-tasks-strategies',
    'describe-greatest-accomplishment-personal-insight',
    'describe-learning-new-process-quickly',
  ],
  [InterviewTypes['System-Design'].key]: [
    'design-google-docs',
    'design-instagram',
    'design-parking-garage-reservation-payment-system',
    'design-rate-limiter',
    'design-chatgpt',
    'design-web-crawler',
    'design-metrics-logging-service',
    'design-typeahead-box-search-engine',
    'design-dropbox',
    'designing-key-value-store',
    'design-uber-eats',
    'design-ticketmaster',
    'design-streaming-service-netflix',
    'design-facebook-messenger',
    'design-system-track-review-abuse-amazon',
    'design-youtube',
    'url-shortener',
    'design-tiktok',
    'design-system-log-messages-in-order',
    'design-system-deny-service-banned-ips',
    'build-tinyurl',
    'design-twitter',
    'design-prime-video',
    'design-evaluation-framework-ads-ranking',
    'design-a-distributed-lru-cache',
    'design-amazon-kindle-payment-system',
    'design-system-ingest-amazon-book-reviews-recommendations',
    'system-design-whatsapp',
    'design-a-document-processing-pipeline',
    'design-product-recommendation-system',
    'design-frequently-listened-songs-list',
    'design-uber',
  ],
  [InterviewTypes['Investment-Banking'].key]: [
    // Valuation
    'explain-dcf-valuation-method',
    'explain-valuation-methodologies-comps-precedents-dcf',
    'explain-common-valuation-approaches-compare',
    'calculate-terminal-value-dcf-drawbacks',
    'explain-gordon-growth-model-terminal-rate',
    'explain-wacc-conceptually',
    'calculate-cost-equity',
    'explain-levered-vs-unlevered-free-cash-flow',
    'calculate-unlevered-free-cash-flow',
    'explain-interest-rates-impact-dcf-valuation',
    'value-company-negative-ebitda',
    'estimate-startup-value-no-history',
    'identify-factors-selecting-comparable-companies',
    'rank-valuation-methodologies-implied-value',
    // Accounting
    'explain-three-financial-statements-linkages',
    'explain-impact-depreciation-increase-tax',
    'describe-equipment-purchase-financial-statements',
    'explain-debt-impact-financial-statements',
    'select-single-financial-statement',
    'explain-cash-vs-accrual-accounting',
    'explain-capex-statements-depreciation-relation',
    'explain-deferred-tax-assets-liabilities',
    'explain-negative-working-capital-scenarios',
    'explain-asset-write-down-three-statements',
    'walk-through-three-statements-revenue-cogs',
    'explain-companies-net-income-ebitda-close',
    // M&A
    'explain-high-level-merger-model',
    'explain-ma-accretive-dilutive',
    'rank-cash-debt-stock-accretive-deal',
    'explain-goodwill-creation-acquisition',
    'analyze-accretive-dilutive-all-stock-deal',
    'explain-dilutive-deal-factors',
    'compare-pros-cons-stock-cash-debt-financing',
    'explain-nonfinancial-factors-accretion-dilution',
    'calculate-exchange-ratio-stock-deal',
    'explain-company-spinoff-reasons',
    'explain-strategic-premium-target-acquisition',
    'calculate-pretax-synergies-break-even-deal',
    // Enterprise / Equity Value
    'explain-enterprise-value-vs-equity-value',
    'explain-equity-value-enterprise-value-bridge',
    'explain-cash-subtracted-equity-enterprise-value',
    'explain-treasury-stock-method',
    'calculate-enterprise-value-financials',
    'compare-ev-ebitda-vs-pe-valuation',
    'explain-enterprise-value-new-debt',
    'calculate-fully-diluted-share-count-options',
    'explain-negative-enterprise-value',
    'calculate-ebitda-margin-ev-multiples',
    // LBO
    'explain-lbo-process-steps',
    'explain-drivers-returns-lbo',
    'define-good-lbo-candidate-qualities',
    'explain-lbo-levers-increase-irr',
    'explain-irr-vs-roi-difference',
    'explain-leverage-use-private-equity',
    'explain-lbo-floor-valuation',
    'explain-dividend-recap',
    'explain-key-line-items-lbo-model',
    'explain-leverage-impact-lbo-irr',
    // Markets
    'pitch-stock-investment-case',
    'explain-interest-rates-bonds-relationship',
    'explain-interest-rates-us-dollar',
    'explain-fed-influence-long-term-rates',
    'describe-market-trends-followed',
    'analyze-recent-deal-buyer-rationale',
    'explain-ipo-process-steps',
    'compare-direct-listing-vs-ipo',
    'predict-sp500-next-12-months',
    'explain-interest-rate-swap',
  ],
};

/**
 * Interview question categories and their mappings, used by the client
 * category picker and the server AI prompt builders
 * @type {object}
 */
const PMCategories = {
  ESTIMATION: {
    key: 'estimation',
    name: 'Estimation',
    description: 'Market sizing and quantitative estimation questions',
    questions: [
      'bikes-needed-launch-bike-sharing-nyc',
      'gmail-messages-per-second',
      'estimate-restaurants-sf',
      'estimate-storage-google-photos',
      'calculating-tennis-balls-in-airplane',
      'empire-state',
    ],
  },
  PRODUCT_DESIGN: {
    key: 'product_design',
    name: 'Product Design',
    description: 'Design new features, products, or user experiences',
    questions: [
      'design-netflix-for-kids',
      'improve-instagram-stories',
      'linkedin-student-product-design',
      'favorite-product',
      'design-parking-features-google-maps',
      'design-fire-alarm-for-deaf',
      'improve-spotify',
    ],
  },
  STRATEGY: {
    key: 'strategy',
    name: 'Strategy',
    description: 'Product strategy, market entry, and business decisions',
    questions: [
      'slack-entering-education-market',
      'impact-faster-internet-speed',
      'android-strategic-google',
      'should-google-enter-online-furniture-market',
      'pm-gmail-react-competing-product',
      'triple-revenue-of-craigslist-in-5-years',
      'meta-reels-invest-strategy-metrics',
      'convince-leadership-elderly-wristwatch',
    ],
  },
  EXECUTION: {
    key: 'execution',
    name: 'Execution',
    description: 'Success metrics, analysis, and execution-focused questions',
    questions: [
      'defining-success-metrics-google-flight-search',
      'measure-success-chatgpt-search',
      'monthly-active-users-decline-analysis',
      'netflix-click-through-rate-analysis',
      'determine-success-instagram-reels',
      'facebook-events-success-metrics',
      'youtube-comments-up-watch-time-down-actions',
    ],
  },
  // GENERAL: {
  //   key: 'general',
  //   name: 'General PM',
  //   description: 'General product management and analysis questions',
  //   questions: [
  //     'company-pain-points-favorite-product',
  //     'favorite-product',
  //   ],
  // },
  // BEHAVIORAL_PM: {
  //   key: 'behavioral_pm',
  //   name: 'PM Behavioral',
  //   description: 'PM-specific behavioral questions',
  //   questions: [
  //     'time-handled-difficult-stakeholder',
  //   ],
  // },
};

// NOTE: Not currently used - just examples to consider
const BehavioralCategories = {
  CONFLICT_RESOLUTION: {
    key: 'conflict_resolution',
    name: 'Conflict Resolution',
    description: 'Handling conflicts and difficult situations',
    questions: [
      'conflict-resolution-learning',
      'example-managing-conflict',
    ],
  },
  TEAMWORK: {
    key: 'teamwork',
    name: 'Teamwork & Collaboration',
    description: 'Working with cross-functional teams and collaboration',
    questions: [
      'experience-cross-functional-team',
    ],
  },
  PRIORITIZATION: {
    key: 'prioritization',
    name: 'Prioritization & Decision Making',
    description: 'Feature prioritization and decision-making processes',
    questions: [
      'how-prioritize-features',
    ],
  },
  GROWTH_LEARNING: {
    key: 'growth_learning',
    name: 'Growth & Learning',
    description: 'Personal growth, learning from mistakes, and development',
    questions: [
      'mistake',
      'personal-growth-approach',
    ],
  },
  PROJECT_DELIVERY: {
    key: 'project_delivery',
    name: 'Project Delivery',
    description: 'Project management and delivery under pressure',
    questions: [
      'project-with-tight-deadline',
      'project-you-are-most-proud-of',
    ],
  },
  GENERAL_BEHAVIORAL: {
    key: 'general_behavioral',
    name: 'General Behavioral',
    description: 'General behavioral and experience questions',
    questions: [
      'past-projects',
    ],
  },
};

const FinanceCategories = {
  VALUATION: {
    key: 'valuation',
    name: 'Valuation',
    description: 'DCF, comps, WACC, and valuation methodologies',
    questions: [
      'explain-dcf-valuation-method',
      'explain-valuation-methodologies-comps-precedents-dcf',
      'explain-common-valuation-approaches-compare',
      'calculate-terminal-value-dcf-drawbacks',
      'explain-gordon-growth-model-terminal-rate',
      'explain-wacc-conceptually',
      'calculate-cost-equity',
      'explain-levered-vs-unlevered-free-cash-flow',
      'calculate-unlevered-free-cash-flow',
      'explain-interest-rates-impact-dcf-valuation',
      'value-company-negative-ebitda',
      'estimate-startup-value-no-history',
      'identify-factors-selecting-comparable-companies',
      'rank-valuation-methodologies-implied-value',
    ],
  },
  ACCOUNTING: {
    key: 'accounting',
    name: 'Accounting',
    description: 'The three statements and how transactions flow through them',
    questions: [
      'explain-three-financial-statements-linkages',
      'explain-impact-depreciation-increase-tax',
      'describe-equipment-purchase-financial-statements',
      'explain-debt-impact-financial-statements',
      'select-single-financial-statement',
      'explain-cash-vs-accrual-accounting',
      'explain-capex-statements-depreciation-relation',
      'explain-deferred-tax-assets-liabilities',
      'explain-negative-working-capital-scenarios',
      'explain-asset-write-down-three-statements',
      'walk-through-three-statements-revenue-cogs',
      'explain-companies-net-income-ebitda-close',
    ],
  },
  MERGERS_AND_ACQUISITIONS: {
    key: 'mergers_and_acquisitions',
    name: 'M&A',
    description: 'Merger models, accretion/dilution, and deal structures',
    questions: [
      'explain-high-level-merger-model',
      'explain-ma-accretive-dilutive',
      'rank-cash-debt-stock-accretive-deal',
      'explain-goodwill-creation-acquisition',
      'analyze-accretive-dilutive-all-stock-deal',
      'explain-dilutive-deal-factors',
      'compare-pros-cons-stock-cash-debt-financing',
      'explain-nonfinancial-factors-accretion-dilution',
      'calculate-exchange-ratio-stock-deal',
      'explain-company-spinoff-reasons',
      'explain-strategic-premium-target-acquisition',
      'calculate-pretax-synergies-break-even-deal',
    ],
  },
  ENTERPRISE_EQUITY_VALUE: {
    key: 'enterprise_equity_value',
    name: 'Enterprise / Equity Value',
    description: 'The EV to equity value bridge, multiples, and dilution',
    questions: [
      'explain-enterprise-value-vs-equity-value',
      'explain-equity-value-enterprise-value-bridge',
      'explain-cash-subtracted-equity-enterprise-value',
      'explain-treasury-stock-method',
      'calculate-enterprise-value-financials',
      'compare-ev-ebitda-vs-pe-valuation',
      'explain-enterprise-value-new-debt',
      'calculate-fully-diluted-share-count-options',
      'explain-negative-enterprise-value',
      'calculate-ebitda-margin-ev-multiples',
    ],
  },
  LBO: {
    key: 'lbo',
    name: 'LBO',
    description: 'Leveraged buyouts, returns drivers, and IRR',
    questions: [
      'explain-lbo-process-steps',
      'explain-drivers-returns-lbo',
      'define-good-lbo-candidate-qualities',
      'explain-lbo-levers-increase-irr',
      'explain-irr-vs-roi-difference',
      'explain-leverage-use-private-equity',
      'explain-lbo-floor-valuation',
      'explain-dividend-recap',
      'explain-key-line-items-lbo-model',
      'explain-leverage-impact-lbo-irr',
    ],
  },
  MARKETS: {
    key: 'markets',
    name: 'Markets',
    description: 'Stock pitches, rates, and current market awareness',
    questions: [
      'pitch-stock-investment-case',
      'explain-interest-rates-bonds-relationship',
      'explain-interest-rates-us-dollar',
      'explain-fed-influence-long-term-rates',
      'describe-market-trends-followed',
      'analyze-recent-deal-buyer-rationale',
      'explain-ipo-process-steps',
      'compare-direct-listing-vs-ipo',
      'predict-sp500-next-12-months',
      'explain-interest-rate-swap',
    ],
  },
};

const QuestionCategoriesByInterviewType = {
  [InterviewTypes.PM.key]: PMCategories,
  [InterviewTypes.Behavioral.key]: BehavioralCategories,
  [InterviewTypes['Investment-Banking'].key]: FinanceCategories,
};

/**
 * Get the category for a given question slug
 */
const getQuestionCategory = (questionSlug, interviewType) => {
  const categories = QuestionCategoriesByInterviewType[interviewType];

  if (categories) {
    const category = Object
      .values(categories)
      .find((cat) => cat.questions.includes(questionSlug));

    if (category) return category.key;
  }

  return null;
};

const QuestionCategoryLessonRecommendation = {
  'product-strategy': {
    product: ['apple-modular-phones', 'samsung-gaming-market', 'food-delivery-time-increase'],
  },
  analytical: {
    product: ['instagram-reels', 'improve-google', 'youtube-metrics'],
  },
  execution: {
    product: ['mock-interview-execution-lyft', 'mock-interview-tiktok-decline', 'facebook-marketplace'],
  },
  'product-design': {
    product: ['mock-interview-facebook-movies', 'improve-headspace', 'design-audio-product-meta'],
  },
  estimation: {
    product: ['shampoo-bottle-sales', 'self-driving-cars', 'airbnb-revenue'],
  },
  technical: {
    product: ['url-shortener', 'technical-google-docs', 'type-url-browser'],
  },
  behavioral: {
    product: ['solving-customer-pain-points', 'consider-your-impact', 'short-term-sacrifices'],
    engineering: ['behavioral-staff-plus-engineer', 'demonstrating-ownership', 'senior-plus-faang'],
    data: ['making-a-decision-with-a-lot-of-uncertainty', 'take-the-lead-in-a-challenging-situation', 'handling-vague-business-problems'],
  },
};

const QuestionCategoryFreeLessonRecommendation = {
  'product-strategy': {
    product: ['how-to-answer-product-strategy-questions'],
  },
  analytical: {
    product: ['intro-analytical'],
  },
  execution: {
    product: ['execution-intro'],
  },
  'product-design': {
    product: ['mock-interview-facebook-movies'],
  },
  estimation: {
    product: ['introduction-estimation-questions', 'estimate-restaurants-sf'],
  },
  technical: {
    product: ['url-shortener', 'technical-google-docs', 'type-url-browser'],
  },
  behavioral: {
    product: ['behavioral-interviews-for-product-managers'],
    engineering: ['eng-behavioral-interviews-intro', 'breaking-interview-rules', 'sounding-like-robot'],
    data: ['making-a-decision-with-a-lot-of-uncertainty', 'take-the-lead-in-a-challenging-situation', 'handling-vague-business-problems'],
  },
};

/**
 * Mapping of category tags to their corresponding 1P AI session type
 * @type {object}
 */
const CategoryInterviewTypeMap = {
  behavioral: 'Behavioral',
  'product-strategy': 'PM',
  'product-design': 'PM',
  estimation: 'PM',
  analytical: 'PM',
  execution: 'PM',
  valuation: 'Investment-Banking',
  accounting: 'Investment-Banking',
  'mergers-and-acquisitions': 'Investment-Banking',
  'enterprise-equity-value': 'Investment-Banking',
  lbo: 'Investment-Banking',
  markets: 'Investment-Banking',
};

/**
 * Orgs that have opted out of having their audio used to train third-party
 * AI transcription models. For these orgs, Deepgram requests are sent with
 * `mip_opt_out=true` (Model Improvement Program opt-out), and Deepgram-backed
 * features (e.g. the "Record your answer" transcription mode in the AI
 * interviewer) may also be hidden in the UI as an extra precaution.
 * @type {object}
 */
const AITrainingOptOutOrgList = {
  yale: true,
  hbs: true,
};

/**
 * Email domains treated the same as membership in an opted-out org, so the
 * opt-out also covers users who signed up with a university email through a
 * personal account rather than org SSO.
 * @type {array}
 */
const AITrainingOptOutEmailDomains = [
  'hbs.edu',
];

/**
 * Defines the possible feedback fields that can be submitted by a
 * user following a Duet session, including deprecated fields which
 * were used in the past. The actual schema for feedback is defined
 * in the feedback forms on the client.
 *
 * Because old feedback remains accessible to users, for backwards
 * compatibility THIS OBJECT IS APPEND ONLY!
 *
 * key                 - Database key in duet_match.feedback. Should match object key.
 * label               - Label displayed to user in feedback form
 * feedbackLabel       - Label displayed to partner in feedback modal
 * isSharedWithPartner - Whether the feedback is shared with the partner
 * [stars]             - Array of strings associated with star ratings. 0 index is 1 star, 1 index is 2 stars, etc.
 * [radio]             - Array of objects with label and value properties for radio buttons
 * [select]            - Array of strings for select dropdown
 * [written]           - Placeholder text for written feedback
 *
 * @type {object}
 */
const FeedbackFields = {
  // Fields used prior to the Pramp integration
  happened: {
    key: 'happened',
    feedbackFormLabel: 'Did this session happen?',
    isSharedWithPartner: false,
    radio: [{label: 'Yes', value: true}, {label: 'No', value: false}],
  },
  satisfaction: {
    key: 'satisfaction',
    label: 'How satisfied were you with your interview?',
    isSharedWithPartner: false,
    stars: [
      'Very dissatisfied',
      'Somewhat dissatisfied',
      'Somewhat satisfied',
      'Very satisfied',
    ],
  },
  improve: {
    key: 'improve',
    feedbackFormLabel: 'How could your partner improve? (This will be shared with your partner)',
    feedbackLabel: 'How could you improve?',
    isSharedWithPartner: true,
    written: 'Comments',
  },
  other: {
    key: 'other',
    feedbackFormLabel: 'Any additional feedback for Aced?',
    isSharedWithPartner: false,
    written: 'What issues did you encounter? How can we improve?',
  },

  // Fields added for the Pramp integration
  problem_solving: {
    key: 'problem_solving',
    feedbackFormLabel: 'How were your partner\'s problem solving skills?',
    feedbackLabel: 'Problem solving',
    description: 'How effectively they broke down the problem and worked toward a correct, efficient solution. Consider their approach and whether they reached an optimal answer.',
    isSharedWithPartner: true,
    stars: [
      'Struggling. Had trouble grasping the problem',
      'Decent. Worked out a rough solution',
      'Good. Solved the problem with hints',
      'Amazing. Optimal solution with minimal guidance',
    ],
  },
  coding: {
    key: 'coding',
    feedbackFormLabel: 'How were your partner\'s coding skills?',
    feedbackLabel: 'Coding skills',
    description: 'The quality of the code they wrote, including correctness, readability, and reusability. Consider how smoothly they translated their ideas into working code.',
    isSharedWithPartner: true,
    stars: [
      'Beginner. Unfamiliar with some of the basics',
      'Decent. Struggled to get from idea to running code',
      'Strong. Could be made more readable or reusable',
      'Expert. Code is readable, reusable, and maintainable',
    ],
  },
  communication: {
    key: 'communication',
    feedbackFormLabel: 'How were your partner\'s communication skills?',
    feedbackLabel: 'Communication',
    description: 'How clearly they explained their thinking throughout the session. Consider whether they thought out loud, structured their answer, and were easy to follow.',
    isSharedWithPartner: true,
    stars: [
      'Weak. Hardly spoke, or was very unclear',
      'Decent. I wasn\'t able to understand some parts',
      'Good. Communicated well',
      'Amazing. Every single bit was crystal clear',
    ],
  },
  do_well: {
    key: 'do_well',
    feedbackFormLabel: 'What did your partner do well during the session?',
    feedbackLabel: 'Things you did well',
    isSharedWithPartner: true,
    written: 'What are your partner\'s strengths? What impressed you?',
  },
  not_good: {
    key: 'not_good',
    feedbackFormLabel: 'What could your partner improve?',
    feedbackLabel: 'Areas where you could improve',
    isSharedWithPartner: true,
    written: 'What should your partner improve? How would you advise them to get better?',
  },
  interviewer: {
    key: 'interviewer',
    feedbackFormLabel: 'How did your partner perform as your interviewer?',
    feedbackLabel: 'Interviewer performance',
    description: 'How well they ran the session as the interviewer. Consider their preparation, the guidance they gave, and the quality of their feedback.',
    isSharedWithPartner: true,
    stars: [
      'Bad',
      'Okay',
      'Good',
      'Great',
    ],
  },
  challenge_stars: {
    key: 'challenge_stars',
    feedbackFormLabel: 'How challenging were the question(s) you were asked?',
    isSharedWithPartner: false,
    stars: [
      'Very easy. Piece of cake, really',
      'Easy. I got it pretty fast',
      'Okay. Medium level of difficulty',
      'Difficult. Really had to think about it',
      'Very difficult. Didn\'t know where to start!',
    ],
  },
  challenge: {
    key: 'challenge',
    feedbackFormLabel: 'How challenging were the question(s) you were asked?',
    isSharedWithPartner: false,
    written: 'Anything else we should know (e.g. have you seen it before, did you like it)?',
  },
  good_match: {
    key: 'good_match',
    feedbackFormLabel: 'How good a match was your partner for the purpose of this session?',
    isSharedWithPartner: false,
    stars: [
      'Awful. Why did you pair us together?!',
      'Bad. Not a good fit for me',
      'Okay. Kind of in between',
      'Good. Decent match',
      'Great. The right match for me',
    ],
  },
  recommend: {
    key: 'recommend',
    feedbackFormLabel: 'Based on today\'s session, how likely are you to recommend Aced\'s peer mock interviews to a friend?',
    isSharedWithPartner: false,
    radio: [
      {label: 0, value: 0}, {label: 1, value: 1}, {label: 2, value: 2}, {label: 3, value: 3}, {label: 4, value: 4}, {label: 5, value: 5}, {label: 6, value: 6}, {label: 7, value: 7}, {label: 8, value: 8}, {label: 9, value: 9}, {label: 10, value: 10},
    ],
  },
  intro: {
    key: 'intro',
    feedbackFormLabel: 'Do you want an email introduction to your partner?',
    isSharedWithPartner: false,
    radio: [{label: 'Yes', value: true}, {label: 'No', value: false}],
  },

  // Fields added just for backfilled Pramp sessions
  competency: {
    key: 'competency',
    feedbackLabel: 'Demonstrating professional competency',
    description: 'Whether they demonstrated the professional competency the role requires. Consider how clear, concise, and specific their answers and examples were.',
    isSharedWithPartner: true,
    stars: [
      'Not quite. Had trouble answering basic questions',
      'Basically. Gave mostly reasonable answers',
      'Mostly. Gave clear answers without examples',
      'Perfectly. Gave clear & concise answers using specific examples',
    ],
  },
  interpersonal_skills: {
    key: 'interpersonal_skills',
    feedbackLabel: 'Positive first impression',
    description: 'The impression they made as a potential colleague. Consider their professionalism, warmth, and ability to build rapport.',
    isSharedWithPartner: true,
    stars: [
      'Not quite. Got off on the wrong foot',
      'Basically. No major red flags',
      'Mostly. Would be happy to meet again',
      'Definitely. Was positively impressed',
    ],
  },
  domain_knowledge: {
    key: 'domain_knowledge',
    feedbackLabel: 'Data science domain knowledge',
    description: 'Their command of data science concepts and methods. Consider whether their analysis was sound and their conclusions delivered real insight.',
    isSharedWithPartner: true,
    stars: [
      'Not ready. Couldn\'t really figure out the problem',
      'Needs some work. Decent conclusions, but not on point',
      'Really good. Some valuable insights, almost there',
      'Amazing. Cracked the problem and delivered value',
    ],
  },

  // Temporary fields while we test the robustnes of the new Aced Practice
  av_issues: {
    key: 'av_issues',
    feedbackFormLabel: 'Did you experience any audio or video issues during today\'s session?',
    isSharedWithPartner: false,
    radio: [{label: 'Yes', value: true}, {label: 'No', value: false}],
  },
  av_issues_text: {
    key: 'av_issues_text',
    feedbackFormLabel: 'Please describe the issue you encountered and what led up to it.',
    isSharedWithPartner: false,
    written: 'The more detail, the better!',
  },
  editor_issues: {
    key: 'editor_issues',
    feedbackFormLabel: 'Did you experience any issues with the code editor during today\'s session?',
    isSharedWithPartner: false,
    radio: [{label: 'Yes', value: true}, {label: 'No', value: false}],
  },
  editor_issues_text: {
    key: 'editor_issues_text',
    feedbackFormLabel: 'Please describe the issue you encountered and what led up to it.',
    isSharedWithPartner: false,
    written: 'The more detail, the better!',
  },

  // NEW
  problem_solving_v2: {
    key: 'problem_solving_v2',
    feedbackFormLabel: 'How were your partner\'s problem solving skills?',
    feedbackLabel: 'Problem solving',
    description: 'How effectively they broke down the problem and worked toward a correct, efficient solution. Consider their approach and whether they reached an optimal answer.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Had trouble grasping the problem',
      'Weak. Worked out a rough solution',
      'Okay. Solved the problem with hints',
      'Strong. Reached an optimal solution',
      'Very Strong. Optimal solution with minimal guidance',
    ],
  },
  coding_v2: {
    key: 'coding_v2',
    feedbackFormLabel: 'How were your partner\'s coding skills?',
    feedbackLabel: 'Coding skills',
    description: 'The quality of the code they wrote, including correctness, readability, and reusability. Consider how smoothly they translated their ideas into working code.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Unfamiliar with some of the basics',
      'Weak. Struggled to get from idea to running code',
      'Okay. Could be made more readable or reusable',
      'Strong. Code is readable, reusable, and maintainable',
      'Very Strong. Would be difficult to improve code',
    ],
  },
  communication_v2: {
    key: 'communication_v2',
    feedbackFormLabel: 'How were your partner\'s communication skills?',
    feedbackLabel: 'Communication',
    description: 'How clearly they explained their thinking throughout the session. Consider whether they thought out loud, structured their answer, and were easy to follow.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Hardly spoke, or was very unclear',
      'Weak. I wasn\'t able to understand some parts',
      'Okay. Mostly communicated well',
      'Strong. Communicated well throughout',
      'Very Strong. Every single bit was crystal clear',
    ],
  },
  interviewer_v2: {
    key: 'interviewer_v2',
    feedbackFormLabel: 'How did your partner perform as your interviewer?',
    feedbackLabel: 'Interviewer performance',
    description: 'How well they ran the session as the interviewer. Consider their preparation, the guidance they gave, and the quality of their feedback.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Did not allow for a productive session',
      'Weak. Was unprepared or unhelpful',
      'Okay. Did an adequate job',
      'Strong. Was thoughtful and prepared',
      'Very Strong. Provided excellent feedback and guidance',
    ],
  },
  product_vision: {
    key: 'product_vision',
    feedbackFormLabel: 'Were they able to articulate a clear product vision?',
    feedbackLabel: 'Product vision',
    description: 'Whether they articulated a clear, compelling vision for the product. Consider how well they connected user needs to a coherent direction for what to build.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Completely lacked a product vision',
      'Weak. Struggled to define or convey a product vision',
      'Okay. Put forward reasonable ideas but lacked clarity',
      'Strong. Clearly articulated a product vision',
      'Very Strong. Communicated a compelling product vision',
    ],
  },
  prioritization: {
    key: 'prioritization',
    feedbackFormLabel: 'Did they effectively prioritize efforts when solving the problem?',
    feedbackLabel: 'Prioritization',
    description: 'How effectively they decided what to focus on when solving the problem. Consider whether they weighed tradeoffs and justified their prioritization decisions.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Failed to prioritize what to focus on',
      'Weak. Had unclear or confusing prioritization',
      'Okay. Made reasonable prioritization decisions',
      'Strong. Prioritized effectively, weighing tradeoffs',
      'Very Strong. Came up with an optimal prioritization',
    ],
  },
  business_strategy: {
    key: 'business_strategy',
    feedbackFormLabel: 'Did they demonstrate a nuanced understanding of the business and market?',
    feedbackLabel: 'Business strategy',
    description: 'Their understanding of the business and market context around the problem. Consider whether their recommendations reflected sound strategic reasoning.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Failed to consider business strategy',
      'Weak. Strategy unclear',
      'Okay. Basic business sense',
      'Strong. Delivered a logical strategy',
      'Very Strong. Demonstrated strategic understanding',
    ],
  },
  data_literacy: {
    key: 'data_literacy',
    feedbackFormLabel: 'Did they identify the relevant metrics and data sources to make informed decisions?',
    feedbackLabel: 'Data literacy',
    description: 'Whether they identified the relevant metrics and data sources to inform their decisions. Consider how effectively they used data to support their reasoning.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Failed to use data',
      'Weak. Struggled to use data',
      'Okay. Showed some ability to use data',
      'Strong. Used data effectively',
      'Very Strong. Extracted valuable data insights',
    ],
  },
  problem_scope: {
    key: 'problem_scope',
    feedbackFormLabel: 'Did they ask clarifying questions and identify important requirements and constraints?',
    feedbackLabel: 'Scoping the problem',
    description: 'How well they scoped the problem before diving into a solution. Consider whether they asked clarifying questions and identified the key requirements and constraints.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Failed to ask clarifying questions',
      'Weak. Didn\'t fully identify requirements',
      'Okay. Identified some requirements',
      'Strong. Thoughtfully identified requirements',
      'Very Strong. Clearly defined requirements',
    ],
  },
  technical_depth: {
    key: 'technical_depth',
    feedbackFormLabel: 'Did they demonstrate technical expertise by deep diving into their solution and discussing tradeoffs?',
    feedbackLabel: 'Technical depth',
    description: 'The depth of technical expertise they demonstrated. Consider whether they dove into the details of their solution and discussed the tradeoffs of their decisions.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Lacked the technical knowledge required',
      'Weak. Struggled to discuss technical concepts',
      'Okay. Definitely areas for improvement',
      'Strong. Articulated technical decisions clearly',
      'Very Strong. Applied deep technical knowledge',
    ],
  },
  scalability: {
    key: 'scalability',
    feedbackFormLabel: 'Did they discuss scaling strategies and address common performance bottlenecks?',
    feedbackLabel: 'Scalability',
    description: 'Whether they addressed how their design scales. Consider their handling of performance bottlenecks and strategies for growing load.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Didn\'t consider scalability',
      'Weak. Overlooked potential scaling issues',
      'Okay. Identified some scaling strategies',
      'Strong. Demonstrated strong understanding',
      'Very Strong. Considered all scalability aspects',
    ],
  },
  competency_v2: {
    key: 'competency_v2',
    feedbackFormLabel: 'Did they demonstrate strong competency, relevant experiences, and skills necessary for the job?',
    feedbackLabel: 'Problem solving',
    description: 'Whether they demonstrated the competency, experience, and skills the job requires. Consider the strength and relevance of the examples they shared.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Failed to communicate experience and skills',
      'Weak. Unclear impression of skills',
      'Okay. Demonstrated some relevant skills',
      'Strong. Clearly communicated relevant skills',
      'Very Strong. Demonstrated strong competency',
    ],
  },
  technical_accuracy: {
    key: 'technical_accuracy',
    feedbackFormLabel: 'Did they demonstrate accurate knowledge of finance concepts and mechanics?',
    feedbackLabel: 'Technical accuracy',
    description: 'The accuracy of their finance knowledge. Consider whether they applied concepts and mechanics correctly and avoided technical errors.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Fundamental concepts were incorrect',
      'Weak. Made notable technical errors',
      'Okay. Mostly accurate with some gaps',
      'Strong. Applied concepts and mechanics correctly',
      'Very Strong. Flawless command of technical material',
    ],
  },
  financial_intuition: {
    key: 'financial_intuition',
    feedbackFormLabel: 'Did they show sound business judgment and connect concepts to real market context?',
    feedbackLabel: 'Financial intuition',
    description: 'Whether they showed sound business judgment beyond memorized answers. Consider how well they explained the reasoning behind answers and connected concepts to real market context.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Demonstrated little financial understanding',
      'Weak. Struggled to explain the "why" behind answers',
      'Okay. Showed some intuition beyond memorization',
      'Strong. Connected concepts to business reality',
      'Very Strong. Demonstrated deep financial insight',
    ],
  },
  accuracy: {
    key: 'accuracy',
    feedbackFormLabel: 'Was their answer technically accurate and complete?',
    feedbackLabel: 'Accuracy',
    description: 'Whether their conclusion is factually correct and covers the must-include concepts, in any phrasing, without material errors.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Conclusion was wrong or mostly off topic',
      'Weak. Made a material error or only partially supported the conclusion',
      'Okay. Right conclusion but thin coverage of key concepts',
      'Strong. Right conclusion covering most key concepts, minor imprecision only',
      'Very Strong. Right conclusion, all key concepts covered, nothing incorrect',
    ],
  },
  structure: {
    key: 'structure',
    feedbackFormLabel: 'Was their answer well-structured and logically ordered?',
    feedbackLabel: 'Structure',
    description: 'Whether they walked through the expected framework in order, stating assumptions before conclusions. Consider whether the listener could follow the logical progression without having to assemble the answer themselves.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. A single unrelated statement or no logical progression at all',
      'Weak. No discernible order, listener has to assemble the answer',
      'Okay. Right pieces present but scrambled order, needed recoveries',
      'Strong. Mostly ordered with one jump or a brief self-correction',
      'Very Strong. Walked the framework in order, assumptions before conclusions',
    ],
  },
  delivery: {
    key: 'delivery',
    feedbackFormLabel: 'Was their delivery clear and concise?',
    feedbackLabel: 'Delivery',
    description: 'How crisply they delivered the answer. Consider whether they led with the answer, kept it concise (30 to 90 seconds), used precise terminology, and avoided filler.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Unclear throughout or did not address the question asked',
      'Weak. Heavy filler, answer buried at the end, terms misused',
      'Okay. Noticeable rambling or vague wording, but the answer came through',
      'Strong. Minor filler or slightly long, terminology correct',
      'Very Strong. Led with the answer, concise, precise terminology, no filler',
    ],
  },
  // Behavioral ("fit") interviews for investment banking candidates
  story_quality: {
    key: 'story_quality',
    feedbackFormLabel: 'Did they tell a real, specific story that demonstrates the trait asked about?',
    feedbackLabel: 'Story quality',
    description: 'Whether the answer is a real, specific experience that demonstrates the trait asked about. Consider whether it has a named role, real stakes, and details only someone who lived it would include.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. No real story, evasive or off topic',
      'Weak. Barely addresses the trait asked about, or answers in hypotheticals',
      'Okay. Relevant but generic, the story could belong to anyone',
      'Strong. Real and relevant, but thin on details or the stakes are modest',
      'Very Strong. Squarely demonstrates the trait, with a named role and real stakes',
    ],
  },
  story_structure: {
    key: 'story_structure',
    feedbackFormLabel: 'Was their story coherent from start to finish?',
    feedbackLabel: 'Structure',
    description: 'Whether the story ran a complete arc. Consider whether the scene was set quickly, their own role was clear, most of the time went to specific actions, and the outcome was stated.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. No discernible arc or progression',
      'Weak. Fragments the listener has to assemble, no individual contribution visible',
      'Okay. The story assembles but jumps around, their role or the outcome stays fuzzy',
      'Strong. Complete arc with one beat thin or briefly out of order',
      'Very Strong. Complete arc, specific actions, outcome stated, "I" rather than "we"',
    ],
  },
  banking_connection: {
    key: 'banking_connection',
    feedbackFormLabel: 'Did they connect the skill they demonstrated to the analyst role?',
    feedbackLabel: 'Banking connection',
    description: 'Whether the answer closes by tying the skill it demonstrated to the analyst seat. This is what separates a banking answer from a generic one.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. The close actively undermines their fit for the seat',
      'Weak. Story ends at the result with no takeaway at all',
      'Okay. Reflects on what they learned but never ties it to the role',
      'Strong. Connection made but generic ("this will help me in banking")',
      'Very Strong. Ties the specific skill shown to the role, naturally and specifically',
    ],
  },
  story_delivery: {
    key: 'story_delivery',
    feedbackFormLabel: 'Was their delivery confident, concise, and free of filler?',
    feedbackLabel: 'Delivery',
    description: 'How crisply they delivered the story. Consider whether they kept it to 60 to 120 seconds and sounded confident and conversational rather than memorized.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Unclear throughout, trails off, or answers a different question',
      'Weak. Heavy filler, monotone script, or the answer is buried at the end',
      'Okay. Noticeable rambling or sounds recited, but the answer lands',
      'Strong. Minor filler, or slightly over or under the window',
      'Very Strong. 60 to 120 seconds, confident and conversational, no filler',
    ],
  },
  collaboration: {
    key: 'collaboration',
    feedbackFormLabel: 'Did they demonstrate the ability to work well with others and collaborate on ideas?',
    feedbackLabel: 'Collaboration',
    description: 'Whether they showed they can work well with others. Consider how they built on ideas, incorporated input, and came across as a teammate.',
    isSharedWithPartner: true,
    stars: [
      'Very weak. Failed to display ability to collaborate',
      'Weak. Unclear picture of how they work with others',
      'Okay. Demonstrated some ability to collaborate',
      'Strong. Clearly communicated ability to work on a team',
      'Very Strong. Presents as an indispensable team player',
    ],
  },
};

/**
 * Types of websockets that can be set up
 */
const SocketConnectionTypes = {
  MATCHING: 'matching',
  CHAT: 'chat',
  CODING_INTERVIEW: 'coding_interview',
  TRANSCRIPTION_AI_SESSION: 'transcription_ai_session',
  CONVERSATIONAL_AI_SESSION: 'conversational_ai_session',
};

/**
 * Event types that can be sent/received by a websocket client
 */
const ClientEvents = {
  CONNECTION: 'connection',
  DISCONNECT: 'disconnect',
  SETUP_COMPLETE: 'setup_complete',
  MATCH: 'match',
  NO_MATCH: 'no_match',
  MATCH_ERROR: 'match_error',
  JOIN_ROOM: 'join_room',
  CHAT_MESSAGE: 'chat_message',
  CODE_RUN: 'code_run',
  CODE_RUN_FINISHED: 'code_run_finished',
  TESTS_RUN: 'tests_run',
  TESTS_RUN_FINISHED: 'tests_run_finished',
  LANGUAGE_CHANGE: 'language_change',
  INTERVIEWER_CHANGE: 'interviewer_change',
  QUESTION_CHANGE: 'question_change',
  TRANSCRIPT: 'transcript',
  AI_AUDIO_GENERATED: 'ai_audio_generated',
  AI_AUDIO_SKIPPED: 'ai_audio_skipped',
  AI_AUDIO_SPOKEN: 'ai_audio_spoken',
  EXTRA_SESSION_STATE_CHANGE: 'extra_session_state_change',
  // Collaborative code editor
  EDITOR_SYNC_REQUEST: 'editor_sync_request',
  EDITOR_SYNC_REPLY: 'editor_sync_reply',
  EDITOR_UPDATE: 'editor_update',
  EDITOR_AWARENESS: 'editor_awareness',
};

const FourTimesADay = [
  // Sunday
  {day: 0, hour: 5, minute: 0}, {day: 0, hour: 11, minute: 0}, {day: 0, hour: 15, minute: 0}, {day: 0, hour: 21, minute: 0},
  // Monday
  {day: 1, hour: 5, minute: 0}, {day: 1, hour: 11, minute: 0}, {day: 1, hour: 15, minute: 0}, {day: 1, hour: 21, minute: 0},
  // Tuesday
  {day: 2, hour: 5, minute: 0}, {day: 2, hour: 11, minute: 0}, {day: 2, hour: 15, minute: 0}, {day: 2, hour: 21, minute: 0},
  // Wednesday
  {day: 3, hour: 5, minute: 0}, {day: 3, hour: 11, minute: 0}, {day: 3, hour: 15, minute: 0}, {day: 3, hour: 21, minute: 0},
  // Thursday
  {day: 4, hour: 5, minute: 0}, {day: 4, hour: 11, minute: 0}, {day: 4, hour: 15, minute: 0}, {day: 4, hour: 21, minute: 0},
  // Friday
  {day: 5, hour: 5, minute: 0}, {day: 5, hour: 11, minute: 0}, {day: 5, hour: 15, minute: 0}, {day: 5, hour: 21, minute: 0},
  // Saturday
  {day: 6, hour: 5, minute: 0}, {day: 6, hour: 11, minute: 0}, {day: 6, hour: 15, minute: 0}, {day: 6, hour: 21, minute: 0},
];

const everyTwoHours = [
  // Sunday
  {day: 0, hour: 1, minute: 0}, {day: 0, hour: 3, minute: 0}, {day: 0, hour: 5, minute: 0}, {day: 0, hour: 7, minute: 0}, {day: 0, hour: 9, minute: 0}, {day: 0, hour: 11, minute: 0}, {day: 0, hour: 13, minute: 0}, {day: 0, hour: 15, minute: 0}, {day: 0, hour: 17, minute: 0}, {day: 0, hour: 19, minute: 0}, {day: 0, hour: 21, minute: 0}, {day: 0, hour: 23, minute: 0},
  // Monday
  {day: 1, hour: 1, minute: 0}, {day: 1, hour: 3, minute: 0}, {day: 1, hour: 5, minute: 0}, {day: 1, hour: 7, minute: 0}, {day: 1, hour: 9, minute: 0}, {day: 1, hour: 11, minute: 0}, {day: 1, hour: 13, minute: 0}, {day: 1, hour: 15, minute: 0}, {day: 1, hour: 17, minute: 0}, {day: 1, hour: 19, minute: 0}, {day: 1, hour: 21, minute: 0}, {day: 1, hour: 23, minute: 0},
  // Tuesday
  {day: 2, hour: 1, minute: 0}, {day: 2, hour: 3, minute: 0}, {day: 2, hour: 5, minute: 0}, {day: 2, hour: 7, minute: 0}, {day: 2, hour: 9, minute: 0}, {day: 2, hour: 11, minute: 0}, {day: 2, hour: 13, minute: 0}, {day: 2, hour: 15, minute: 0}, {day: 2, hour: 17, minute: 0}, {day: 2, hour: 19, minute: 0}, {day: 2, hour: 21, minute: 0}, {day: 2, hour: 23, minute: 0},
  // Wednesday
  {day: 3, hour: 1, minute: 0}, {day: 3, hour: 3, minute: 0}, {day: 3, hour: 5, minute: 0}, {day: 3, hour: 7, minute: 0}, {day: 3, hour: 9, minute: 0}, {day: 3, hour: 11, minute: 0}, {day: 3, hour: 13, minute: 0}, {day: 3, hour: 15, minute: 0}, {day: 3, hour: 17, minute: 0}, {day: 3, hour: 19, minute: 0}, {day: 3, hour: 21, minute: 0}, {day: 3, hour: 23, minute: 0},
  // Thursday
  {day: 4, hour: 1, minute: 0}, {day: 4, hour: 3, minute: 0}, {day: 4, hour: 5, minute: 0}, {day: 4, hour: 7, minute: 0}, {day: 4, hour: 9, minute: 0}, {day: 4, hour: 11, minute: 0}, {day: 4, hour: 13, minute: 0}, {day: 4, hour: 15, minute: 0}, {day: 4, hour: 17, minute: 0}, {day: 4, hour: 19, minute: 0}, {day: 4, hour: 21, minute: 0}, {day: 4, hour: 23, minute: 0},
  // Friday
  {day: 5, hour: 1, minute: 0}, {day: 5, hour: 3, minute: 0}, {day: 5, hour: 5, minute: 0}, {day: 5, hour: 7, minute: 0}, {day: 5, hour: 9, minute: 0}, {day: 5, hour: 11, minute: 0}, {day: 5, hour: 13, minute: 0}, {day: 5, hour: 15, minute: 0}, {day: 5, hour: 17, minute: 0}, {day: 5, hour: 19, minute: 0}, {day: 5, hour: 21, minute: 0}, {day: 5, hour: 23, minute: 0},
  // Saturday
  {day: 6, hour: 1, minute: 0}, {day: 6, hour: 3, minute: 0}, {day: 6, hour: 5, minute: 0}, {day: 6, hour: 7, minute: 0}, {day: 6, hour: 9, minute: 0}, {day: 6, hour: 11, minute: 0}, {day: 6, hour: 13, minute: 0}, {day: 6, hour: 15, minute: 0}, {day: 6, hour: 17, minute: 0}, {day: 6, hour: 19, minute: 0}, {day: 6, hour: 21, minute: 0}, {day: 6, hour: 23, minute: 0},
];

const SQLBetaHours = [
  // Sunday
  {day: 0, hour: 11, minute: 0}, {day: 0, hour: 21, minute: 0},
  // Monday
  {day: 1, hour: 11, minute: 0}, {day: 1, hour: 21, minute: 0},
  // Tuesday
  {day: 2, hour: 11, minute: 0}, {day: 2, hour: 21, minute: 0},
  // Wednesday
  {day: 3, hour: 11, minute: 0}, {day: 3, hour: 21, minute: 0},
  // Thursday
  {day: 4, hour: 11, minute: 0}, {day: 4, hour: 21, minute: 0},
  // Friday
  {day: 5, hour: 11, minute: 0}, {day: 5, hour: 21, minute: 0},
  // Saturday
  {day: 6, hour: 11, minute: 0}, {day: 6, hour: 21, minute: 0},
];

const DataScienceMLBetaHours = [
  // Sunday
  {day: 0, hour: 9, minute: 0}, {day: 0, hour: 19, minute: 0},
  // Monday
  {day: 1, hour: 9, minute: 0}, {day: 1, hour: 19, minute: 0},
  // Tuesday
  {day: 2, hour: 9, minute: 0}, {day: 2, hour: 19, minute: 0},
  // Wednesday
  {day: 3, hour: 9, minute: 0}, {day: 3, hour: 19, minute: 0},
  // Thursday
  {day: 4, hour: 9, minute: 0}, {day: 4, hour: 19, minute: 0},
  // Friday
  {day: 5, hour: 9, minute: 0}, {day: 5, hour: 19, minute: 0},
  // Saturday
  {day: 6, hour: 9, minute: 0}, {day: 6, hour: 19, minute: 0},
];

const InvestmentBankingHours = [
  // Sunday
  {day: 0, hour: 11, minute: 0},
  // Wednesday
  {day: 3, hour: 11, minute: 0},
];

const DuetSchedules = {
  [InterviewTypes.PM.key]: FourTimesADay,
  [InterviewTypes['System-Design'].key]: FourTimesADay,
  [InterviewTypes.Behavioral.key]: FourTimesADay,
  [InterviewTypes.Algorithms.key]: everyTwoHours,
  [InterviewTypes.SQL.key]: SQLBetaHours,
  [InterviewTypes['Data-Science-ML'].key]: DataScienceMLBetaHours,
  [InterviewTypes.Frontend.key]: DataScienceMLBetaHours,
  [InterviewTypes['Investment-Banking'].key]: InvestmentBankingHours,
  [InterviewTypes['Behavioral-IB'].key]: [],
};

module.exports = {
  TIMEZONE,
  MAX_REJOIN_TIME,
  MAX_FEEDBACK_TIME,
  LEADING_JOIN_TIME,
  TRAILING_MATCH_TIMES,
  WINDOW_START_TIME,
  DUET_CREDIT_TTL,
  SCREENSHARE_TRACK,
  DUET_SESSIONS_PER_PERIOD,
  MAX_MONTHLY_AI_TRANSCRIPTIONS,
  MAX_MONTHLY_AI_SESSIONS,
  CreditRefundReasons,
  InterviewTypesArray,
  getInterviewTypesForUser,
  isInterviewTypeAvailableToUser,
  resolveInterviewTypeForUser,
  InterviewTypes,
  InterviewTypesWith1PAIQuestionsFromCMS,
  InterviewModes,
  RequestStatus,
  PartnerIntroStatus,
  AIFeedbackStatus,
  AISessionStatus,
  AISessionCreationErrors,
  AISessionVersions,
  AISessionQuestionAllowlist,
  CategoryInterviewTypeMap,
  PMCategories,
  BehavioralCategories,
  FinanceCategories,
  getQuestionCategory,
  AITrainingOptOutOrgList,
  AITrainingOptOutEmailDomains,
  QuestionCategoryLessonRecommendation,
  QuestionCategoryFreeLessonRecommendation,
  FeedbackFields,
  SocketConnectionTypes,
  ClientEvents,
  DuetSchedules,
};
